import { useAuth } from '../../hooks/useAuth';
import { Bell, User, LogOut, Key, Menu } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

type NavbarProps = {
  toggleSidebar?: () => void;
  showMenuButton?: boolean;
};

const Navbar = ({ toggleSidebar, showMenuButton = true }: NavbarProps) => {
  const { user, logout, userRole } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const userMenuRef = useRef<HTMLDivElement>(null);

  const toggleUserMenu = () => setUserMenuOpen((v) => !v);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close user menu on navigation
  useEffect(() => {
    setUserMenuOpen(false);
  }, [location.pathname]);

  const getRoleDashboardPath = () => {
    switch (userRole) {
      case 'admin':
        return '/admin';
      case 'formateur':
        return '/trainer';
      case 'etudiant':
        return '/student';
      default:
        return '/dashboard';
    }
  };

  return (
    <nav className="bg-white border-b border-gray-200 px-4 py-3 shadow-sm sticky top-0 z-30">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          {user && showMenuButton && (
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-md text-gray-500 hover:bg-gray-100 md:hidden"
              aria-label="Ouvrir le menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          )}
          <Link to={user ? getRoleDashboardPath() : '/login'} className="flex items-center">
            <span className="text-lg font-semibold text-primary-600">FormaNote</span>
            <span className="text-sm text-gray-400 ml-2 hidden sm:inline">by FormaVerse</span>
          </Link>
        </div>

        {user && (
          <div className="flex items-center space-x-2">
            <button className="p-2 rounded-full hover:bg-gray-100" aria-label="Notifications">
              <Bell className="h-5 w-5 text-gray-500" />
            </button>

            <div className="relative" ref={userMenuRef}>
              <button
                onClick={toggleUserMenu}
                className="flex items-center space-x-2 p-2 rounded-full hover:bg-gray-100 focus:outline-none"
              >
                <div className="bg-primary-100 text-primary-700 rounded-full p-1">
                  <User className="h-5 w-5" />
                </div>
                <span className="hidden md:inline text-sm font-medium max-w-[200px] truncate">
                  {user.email}
                </span>
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-50 animate-slide-down border border-gray-100">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900 truncate">{user.email}</p>
                    <p className="text-xs text-gray-500 capitalize mt-0.5">{userRole || 'Utilisateur'}</p>
                  </div>

                  <Link
                    to="/change-password"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <div className="flex items-center space-x-2">
                      <Key className="h-4 w-4" />
                      <span>Changer mot de passe</span>
                    </div>
                  </Link>

                  <button
                    onClick={logout}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <div className="flex items-center space-x-2">
                      <LogOut className="h-4 w-4" />
                      <span>Se déconnecter</span>
                    </div>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
